<template>
    <div class="ui-page">
        <header class="ui-page-header">
            <router-link to="/processo-competitivo/processos" class="ui-eyebrow"><v-icon small>mdi-arrow-left</v-icon> Processos competitivos</router-link>
            <div class="ui-page-header__row">
                <div class="ui-page-header__text">
                    <div v-if="process" class="cp-card__slug">/{{ process.slug }}</div>
                    <div class="d-flex align-center flex-wrap" style="gap: 10px;">
                        <h1 class="ui-title">{{ isNew ? "Novo processo" : process ? process.name : "Carregando..." }}</h1>
                        <ui-badge v-if="process" set="cp-process" :status="process.status" />
                    </div>
                    <p v-if="process" class="ui-subtitle">{{ process.seller_name || (process.client && process.client.name) }}<span v-if="process.client && process.client.type_label"> · {{ process.client.type_label }}</span></p>
                </div>
                <div class="d-flex align-center" style="gap: 8px;">
                    <v-btn v-if="process && process.status === 'draft' && $allowed('delete')" outlined class="ui-btn-danger" @click="confirmRemove">Excluir</v-btn>
                    <v-btn v-if="![2, 4].includes(tab) && $allowed('save')" color="primary" depressed :loading="saving" :disabled="!canSave" @click="save">
                        <v-icon left small>mdi-content-save-outline</v-icon>{{ isNew ? "Criar processo" : "Salvar alterações" }}
                    </v-btn>
                </div>
            </div>
            <div v-if="process" class="d-flex flex-wrap mt-3" style="gap: 8px;">
                <v-btn small outlined :to="scoped('/processo-competitivo/painel')"><v-icon left small>mdi-view-dashboard-outline</v-icon>Painel</v-btn>
                <v-btn small outlined :to="scoped('/processo-competitivo/ativos')"><v-icon left small>mdi-domain</v-icon>Ativos · {{ process.stats.assets }}</v-btn>
                <v-btn small outlined :to="scoped('/processo-competitivo/cestas-e-lotes')"><v-icon left small>mdi-layers-outline</v-icon>Cestas e lotes</v-btn>
                <v-btn small outlined :to="scoped('/processo-competitivo/etapas')"><v-icon left small>mdi-format-list-numbered</v-icon>Etapas · {{ process.stats.stages }}</v-btn>
                <v-btn small outlined :to="scoped('/processo-competitivo/inscricoes')"><v-icon left small>mdi-account-check-outline</v-icon>Inscrições</v-btn>
                <v-btn small outlined :to="scoped('/processo-competitivo/data-room')"><v-icon left small>mdi-folder-lock-outline</v-icon>Data room</v-btn>
            </div>
        </header>

        <div v-if="loading" class="ui-empty"><v-progress-circular indeterminate size="28" color="primary"></v-progress-circular></div>
        <template v-else>
            <v-tabs v-model="tab" show-arrows>
                <v-tab>Dados gerais</v-tab>
                <v-tab>Conteúdo da vitrine</v-tab>
                <v-tab :disabled="isNew">Documentos<span v-if="documents.length" class="ui-tab-count">{{ documents.length }}</span></v-tab>
                <v-tab :disabled="isNew">Habilitação</v-tab>
                <v-tab :disabled="isNew">Custódia da chave</v-tab>
            </v-tabs>

            <v-tabs-items v-model="tab" class="pt-5">
                <v-tab-item>
                    <v-row>
                        <v-col cols="12" md="7">
                            <ui-section title="Identificação" description="Como o processo aparece na vitrine e nos documentos.">
                                <v-autocomplete v-model="form.client_id" :items="options.clients" item-value="id" item-text="name" label="Caso" outlined dense hide-details="auto" class="mb-4"
                                    :menu-props="{ offsetY: true }" no-data-text="Nenhum caso">
                                    <template v-slot:item="{ item }">
                                        <div class="py-1"><div class="ui-cell-main">{{ item.name }}</div><div class="ui-cell-sub">{{ item.type_label }}<span v-if="item.process_number"> · {{ item.process_number }}</span></div></div>
                                    </template>
                                </v-autocomplete>
                                <v-text-field v-model="form.name" label="Nome do processo" outlined dense hide-details="auto" class="mb-4" maxlength="255" @input="suggestSlug"></v-text-field>
                                <v-text-field v-model="form.slug" label="Código do endereço público" outlined dense hide-details="auto" class="mb-1" maxlength="80"
                                    :prefix="`${publicHost}/processos/`" @input="slugTouched = true"></v-text-field>
                                <p class="ui-small ui-muted mb-4">Letras minúsculas, números e hífen. Mudar o código depois de divulgado quebra os links já enviados.</p>
                                <v-text-field v-model="form.seller_name" label="Comitente (como aparece na vitrine)" outlined dense hide-details="auto" class="mb-4" maxlength="255"
                                    placeholder="Ex.: Cooperativa Agroindustrial Exemplo Ltda. - em liquidação"></v-text-field>
                                <v-textarea v-model="form.summary" label="Resumo" outlined dense rows="2" auto-grow counter="500" maxlength="500" hide-details="auto" class="mb-1"></v-textarea>
                                <p class="ui-small ui-muted mb-4">Uma ou duas frases para o cartão da vitrine.</p>
                                <v-textarea v-model="form.description" label="Descrição" outlined dense rows="6" auto-grow hide-details="auto"></v-textarea>
                            </ui-section>
                        </v-col>
                        <v-col cols="12" md="5">
                            <ui-section title="Situação e prazos" description="Horário de Brasília.">
                                <v-select v-model="form.status" :items="options.process_statuses" label="Situação" outlined dense hide-details="auto" class="mb-4" :menu-props="{ offsetY: true }"></v-select>
                                <v-text-field v-model="form.registration_ends_at" type="datetime-local" label="Fim das inscrições" outlined dense hide-details="auto" class="mb-4"></v-text-field>
                                <v-autocomplete v-model="form.responsible_admin_id" :items="options.admins" item-value="id" item-text="name" label="Responsável na B&J" clearable outlined dense hide-details="auto" class="mb-2" :menu-props="{ offsetY: true }"></v-autocomplete>
                                <v-switch v-model="form.featured" inset dense hide-details label="Destaque na vitrine" class="mt-2"></v-switch>
                            </ui-section>
                            <ui-section v-if="!isNew" title="Imagem de capa" description="Aparece no cartão do processo. JPG, PNG ou WEBP de até 20 MB." class="mt-4">
                                <ui-file-drop :file="cover.file" :current-url="process && process.cover_url ? process.cover_url : null" accept="image/png,image/jpeg,image/webp" :max-size-mb="20"
                                    hint="JPG, PNG ou WEBP de até 20 MB" :loading="cover.saving" @select="cover.file = $event" @save="saveCover"
                                    @error="(message) => notifyWarning(message)" />
                                <v-progress-linear v-if="cover.saving" :value="cover.progress" height="4" rounded color="primary" class="mt-2"></v-progress-linear>
                                <v-btn v-if="process && process.cover_url && !cover.file && $allowed('save')" small text class="mt-2 ui-btn-danger" @click="removeCover">Remover capa</v-btn>
                            </ui-section>
                        </v-col>
                    </v-row>
                </v-tab-item>

                <v-tab-item>
                    <ui-section title="Conteúdo da vitrine" description="Texto da página pública do processo: como participar, regras principais, contatos e avisos. A vitrine entra no ar na fase do app público.">
                        <v-textarea v-model="form.showcase_content" outlined rows="16" auto-grow hide-details="auto"
                            placeholder="Ex.: Como participar&#10;1. Faça a inscrição e envie os documentos exigidos no edital.&#10;2. Depois de habilitado, acesse o data room..."></v-textarea>
                    </ui-section>
                </v-tab-item>

                <v-tab-item>
                    <ui-table-card>
                        <template #toolbar>
                            <div>
                                <div class="ui-section-title">Documentos do processo</div>
                                <div class="ui-section-description">Edital, laudos, decisões e atas. O SHA-256 de cada PDF fica registrado para conferência.</div>
                            </div>
                            <span class="ui-toolbar__spacer"></span>
                            <v-btn v-if="$allowed('save')" color="primary" depressed @click="openDocument(null)"><v-icon left small>mdi-plus</v-icon>Adicionar documento</v-btn>
                        </template>
                        <v-data-table :headers="documentHeaders" :items="documents" disable-sort disable-pagination hide-default-footer no-data-text="Nenhum documento enviado">
                            <template v-slot:[`item.title`]="{ item }">
                                <div class="ui-cell-main">{{ item.title }}</div>
                                <div class="ui-cell-sub">{{ item.file_name }} · {{ formatBytes(item.file_size) }}</div>
                            </template>
                            <template v-slot:[`item.category_label`]="{ item }">{{ item.category_label }}</template>
                            <template v-slot:[`item.public`]="{ item }"><ui-badge :text="item.public ? 'Pública' : 'Interna'" :tone="item.public ? 'info' : 'neutral'" /></template>
                            <template v-slot:[`item.file_hash`]="{ item }"><span class="ui-mono ui-small" :title="item.file_hash">{{ item.file_hash ? `${item.file_hash.slice(0, 12)}…` : "—" }}</span></template>
                            <template v-slot:[`item.actions`]="{ item }">
                                <ui-row-actions :actions="[
                                    { text: 'Baixar', icon: 'mdi-download', handler: () => downloadDocument(item) },
                                    { text: 'Editar', icon: 'mdi-pencil-outline', handler: () => openDocument(item), disabled: !$allowed('save') },
                                    { text: 'Excluir', icon: 'mdi-delete-outline', handler: () => removeDocument(item), danger: true, disabled: !$allowed('save') },
                                ]" />
                            </template>
                        </v-data-table>
                    </ui-table-card>
                </v-tab-item>

                <v-tab-item>
                    <template v-if="process">
                        <competitive-required-documents :process-id="process.id" :options="options" />
                        <v-row class="mt-2">
                            <v-col cols="12" md="7">
                                <ui-section title="Termo de confidencialidade (NDA)" :description="ndaDescription">
                                    <v-textarea v-model="form.nda_content" outlined rows="12" auto-grow hide-details="auto"
                                        placeholder="Texto que o habilitado assina antes de acessar o data room."></v-textarea>
                                    <p class="ui-small ui-muted mt-2 mb-0">Ao salvar um texto diferente, a versão sobe e quem assinou a anterior precisa assinar de novo para voltar ao data room.</p>
                                </ui-section>
                                <ui-section title="Termo de inscrição" :description="termDescription" class="mt-4">
                                    <v-textarea v-model="form.term_content" outlined rows="10" auto-grow hide-details="auto" :placeholder="process.term_default"></v-textarea>
                                    <div class="d-flex align-center flex-wrap mt-2" style="gap: 8px;">
                                        <p class="ui-small ui-muted mb-0" style="flex: 1 1 260px;">Em branco, vale o texto padrão (em cinza). Separe os parágrafos com uma linha em branco. Ao mudar o texto, a versão sobe e quem ainda não enviou a inscrição assina de novo.</p>
                                        <v-btn v-if="!form.term_content" small outlined @click="form.term_content = process.term_default">Editar a partir do padrão</v-btn>
                                        <v-btn v-else small text @click="form.term_content = ''">Voltar ao padrão</v-btn>
                                    </div>
                                </ui-section>
                            </v-col>
                            <v-col cols="12" md="5">
                                <ui-section title="Caução" description="Opcional por processo (R1). Quando exigida, cadastre o comprovante como exigência do tipo caução.">
                                    <v-switch v-model="form.deposit_required" inset dense hide-details label="Exigir caução na inscrição" class="mt-0 mb-4"></v-switch>
                                    <template v-if="form.deposit_required">
                                        <v-text-field v-model="form.deposit_amount" label="Valor da caução" prefix="R$" outlined dense hide-details="auto" class="mb-4"></v-text-field>
                                        <v-textarea v-model="form.deposit_instructions" label="Como depositar" outlined dense rows="4" auto-grow hide-details="auto"></v-textarea>
                                    </template>
                                </ui-section>
                            </v-col>
                        </v-row>
                    </template>
                </v-tab-item>

                <v-tab-item>
                    <competitive-custodians v-if="process" :process-id="process.id" :options="options" />
                </v-tab-item>
            </v-tabs-items>
        </template>

        <v-dialog v-model="doc.open" max-width="560" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header"><h3 class="ui-section-title">{{ doc.id ? "Editar documento" : "Adicionar documento" }}</h3></div>
                <div class="ui-card__body">
                    <v-text-field v-model="doc.form.title" label="Título" outlined dense hide-details="auto" class="mb-4" maxlength="255"></v-text-field>
                    <v-select v-model="doc.form.category" :items="options.document_categories" label="Categoria" outlined dense hide-details="auto" class="mb-4" :menu-props="{ offsetY: true }"></v-select>
                    <v-switch v-model="doc.form.public" inset dense hide-details label="Mostrar na vitrine" class="mt-0 mb-4"></v-switch>
                    <ui-file-drop v-if="!doc.id" :file="doc.file" accept="application/pdf" :max-size-mb="200" hint="PDF de até 200 MB" :loading="doc.saving"
                        @select="doc.file = $event" @save="saveDocument" @error="(message) => notifyWarning(message)" />
                    <v-progress-linear v-if="doc.saving && doc.file" :value="doc.progress" height="4" rounded color="primary" class="mt-2"></v-progress-linear>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="doc.saving" @click="doc.open = false">Cancelar</v-btn>
                    <v-btn color="primary" depressed :loading="doc.saving" :disabled="!doc.form.title || (!doc.id && !doc.file)" @click="saveDocument">Salvar</v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import CompetitiveRequiredDocuments from "@/components/competitive/CompetitiveRequiredDocuments.vue";
import CompetitiveCustodians from "@/components/competitive/CompetitiveCustodians.vue";
import { API, formatBytes, loadOptions, moneyInput, notifyError, notifySuccess, openUrl, toInputDateTime, uploadForRecord } from "@/components/competitive/competitive.util";

const EMPTY = { client_id: null, name: "", slug: "", seller_name: "", summary: "", description: "", showcase_content: "", status: "draft", featured: false, registration_ends_at: "", responsible_admin_id: null, nda_content: "", term_content: "", deposit_required: false, deposit_amount: "", deposit_instructions: "" };

export default {
    name: "CompetitiveProcessDetails",
    components: { CompetitiveRequiredDocuments, CompetitiveCustodians },
    data: () => ({
        process: null,
        loading: false,
        saving: false,
        tab: 0,
        slugTouched: false,
        publicHost: "ativos.brizolaejapur.com.br",
        options: { clients: [], admins: [], process_statuses: [], document_categories: [] },
        form: { ...EMPTY },
        documents: [],
        documentHeaders: [
            { text: "Documento", value: "title" },
            { text: "Categoria", value: "category_label", width: 170 },
            { text: "Vitrine", value: "public", width: 110 },
            { text: "SHA-256", value: "file_hash", width: 150 },
            { text: "", value: "actions", align: "end", width: 48 },
        ],
        cover: { file: null, saving: false, progress: 0 },
        doc: { open: false, id: null, file: null, saving: false, progress: 0, form: {} },
    }),
    computed: {
        id() {
            return this.$route.params.id;
        },
        isNew() {
            return ["novo", "new"].includes(this.id);
        },
        ndaDescription() {
            if (!this.process || !this.process.nda_version) return "Ainda não publicado: sem ele, nenhum habilitado acessa o data room (R9).";
            return `Versão ${this.process.nda_version} publicada. O habilitado assina com certificado ICP-Brasil A1 (e-CPF ou e-CNPJ); o PDF assinado fica na inscrição.`;
        },
        termDescription() {
            if (!this.process) return "";
            const origin = this.process.term_content ? "texto próprio do processo" : "texto padrão da plataforma";
            return `Versão ${this.process.term_version}, ${origin}. O proponente assina com certificado ICP-Brasil A1 antes de enviar a inscrição.`;
        },
        canSave() {
            return !!this.form.client_id && !!String(this.form.name || "").trim();
        },
    },
    watch: {
        id() {
            this.load();
        },
    },
    async created() {
        try {
            this.options = await loadOptions();
        } catch (error) {
            notifyError(this, error);
        }
        // Atalho do painel: ?aba=habilitacao.
        if (this.$route.query.aba === "habilitacao") this.tab = 3;
        if (this.$route.query.aba === "custodia") this.tab = 4;
        this.load();
    },
    methods: {
        formatBytes,
        notifyWarning(message) {
            this.$dialog.notify.warning(message, { position: "top-right", timeout: 4000 });
        },
        scoped(path) {
            return { path, query: { processo: String(this.process.id) } };
        },
        async load() {
            if (this.isNew) {
                this.process = null;
                this.form = { ...EMPTY };
                this.slugTouched = false;
                this.documents = [];
                return;
            }
            this.loading = true;
            try {
                const result = await axios.get(`${API}/processes/${this.id}`);
                this.fill(result.data.content);
                await this.loadDocuments();
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.loading = false;
            }
        },
        fill(process) {
            this.process = process;
            this.slugTouched = true;
            this.form = {
                client_id: process.client ? process.client.id : null,
                name: process.name,
                slug: process.slug,
                seller_name: process.seller_name || "",
                summary: process.summary || "",
                description: process.description || "",
                showcase_content: process.showcase_content || "",
                status: process.status,
                featured: process.featured,
                registration_ends_at: toInputDateTime(process.registration_ends_at),
                responsible_admin_id: process.responsible ? process.responsible.id : null,
                nda_content: process.nda_content || "",
                term_content: process.term_content || "",
                deposit_required: process.deposit_required,
                deposit_amount: moneyInput(process.deposit_amount),
                deposit_instructions: process.deposit_instructions || "",
            };
        },
        // Codigo do endereco acompanha o nome ate ser editado a mao.
        suggestSlug() {
            if (this.slugTouched) return;
            this.form.slug = String(this.form.name || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);
        },
        async save() {
            this.saving = true;
            try {
                const payload = { ...this.form, registration_ends_at: this.form.registration_ends_at || null };
                if (this.isNew) {
                    const result = await axios.post(`${API}/processes`, payload);
                    notifySuccess(this, "Processo criado.");
                    this.$router.replace({ path: `/processo-competitivo/processos/${result.data.content.id}` });
                } else {
                    const result = await axios.patch(`${API}/processes/${this.process.id}`, payload);
                    this.fill(result.data.content);
                    notifySuccess(this, "Alterações salvas.");
                }
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.saving = false;
            }
        },
        async confirmRemove() {
            const confirmed = await this.$dialog.confirm({
                title: "Excluir processo",
                text: `"${this.process.name}" e os seus ativos, agrupamentos e etapas saem do sistema. Só processos em rascunho podem ser excluídos. Confirma?`,
                actions: { false: "Cancelar", true: { text: "Excluir", color: "error" } },
            });
            if (!confirmed) return;
            try {
                await axios.delete(`${API}/processes/${this.process.id}`);
                notifySuccess(this, "Processo excluído.");
                this.$router.push({ path: "/processo-competitivo/processos" });
            } catch (error) {
                notifyError(this, error);
            }
        },
        async saveCover() {
            if (!this.cover.file) return;
            this.cover.saving = true;
            this.cover.progress = 0;
            try {
                const file = await uploadForRecord(this.cover.file, (percent) => { this.cover.progress = percent; });
                const result = await axios.put(`${API}/processes/${this.process.id}/cover`, file);
                this.process = result.data.content;
                this.cover.file = null;
                notifySuccess(this, "Capa atualizada.");
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.cover.saving = false;
            }
        },
        async removeCover() {
            try {
                const result = await axios.delete(`${API}/processes/${this.process.id}/cover`);
                this.process = result.data.content;
            } catch (error) {
                notifyError(this, error);
            }
        },
        async loadDocuments() {
            const result = await axios.get(`${API}/processes/${this.process.id}/documents`);
            this.documents = result.data.content;
        },
        openDocument(item) {
            this.doc = {
                open: true,
                id: item ? item.id : null,
                file: null,
                saving: false,
                progress: 0,
                form: item ? { title: item.title, category: item.category, public: item.public } : { title: "", category: "notice", public: true },
            };
        },
        async saveDocument() {
            this.doc.saving = true;
            try {
                if (this.doc.id) {
                    await axios.patch(`${API}/documents/${this.doc.id}`, this.doc.form);
                } else {
                    const file = await uploadForRecord(this.doc.file, (percent) => { this.doc.progress = percent; });
                    await axios.post(`${API}/processes/${this.process.id}/documents`, { ...this.doc.form, ...file });
                }
                notifySuccess(this, "Documento salvo.");
                this.doc.open = false;
                await this.loadDocuments();
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.doc.saving = false;
            }
        },
        async downloadDocument(item) {
            try {
                const result = await axios.get(`${API}/documents/${item.id}/url`);
                openUrl(result.data.content.url);
            } catch (error) {
                notifyError(this, error);
            }
        },
        async removeDocument(item) {
            const confirmed = await this.$dialog.confirm({
                title: "Excluir documento",
                text: `"${item.title}" sai do processo e da vitrine. Confirma?`,
                actions: { false: "Cancelar", true: { text: "Excluir", color: "error" } },
            });
            if (!confirmed) return;
            try {
                await axios.delete(`${API}/documents/${item.id}`);
                await this.loadDocuments();
            } catch (error) {
                notifyError(this, error);
            }
        },
    },
};
</script>

<style lang="scss">
@import "../../components/competitive/competitive";
</style>
